import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Workflow, 
  FileText, 
  Scale, 
  Shield, 
  Search, 
  CheckCircle2,
  Clock,
  TrendingUp,
  Zap,
  Play,
  BookOpen,
  AlertTriangle,
  FileCheck,
  GitCompare,
  Building2,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Workflows({ user, cabinet, onLogout }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [recentWorkflows, setRecentWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [workflowInputs, setWorkflowInputs] = useState({});
  const [workflowRunning, setWorkflowRunning] = useState(false);
  const [workflowResults, setWorkflowResults] = useState(null);

  // Workflows prêts à l'emploi
  const workflows = [
    {
      id: "due-diligence",
      name: "Due Diligence Automatique",
      category: "transactionnel",
      description: "Analyse complète de documents juridiques pour opérations M&A : identification des risques, points d'attention, et recommandations.",
      icon: FileCheck,
      color: "blue",
      estimatedTime: "15-30 min",
      steps: 5,
      inputs: ["Documents de la cible", "Périmètre d'analyse"],
      outputs: ["Rapport de due diligence", "Liste des risques", "Recommandations"]
    },
    {
      id: "contract-risk-analysis",
      name: "Analyse de Risques Contractuels",
      category: "transactionnel",
      description: "Identification automatique des clauses à risque dans les contrats : pénalités, résiliation, garanties, responsabilités.",
      icon: AlertTriangle,
      color: "red",
      estimatedTime: "5-10 min",
      steps: 3,
      inputs: ["Contrat à analyser"],
      outputs: ["Carte des risques", "Clauses sensibles", "Score de risque"]
    },
    {
      id: "gdpr-compliance-review",
      name: "Revue de Conformité RGPD",
      category: "conformite",
      description: "Audit automatique de conformité RGPD : traitement des données, consentements, droits des personnes, sécurité.",
      icon: Shield,
      color: "green",
      estimatedTime: "20-40 min",
      steps: 6,
      inputs: ["Documents DPO", "Registre de traitement"],
      outputs: ["Rapport de conformité", "Points de non-conformité", "Plan d'action"]
    },
    {
      id: "contract-comparison",
      name: "Comparaison de Contrats",
      category: "transactionnel",
      description: "Comparaison détaillée de deux versions de contrat : différences, modifications importantes, impact juridique.",
      icon: GitCompare,
      color: "purple",
      estimatedTime: "5-10 min",
      steps: 2,
      inputs: ["Version 1", "Version 2"],
      outputs: ["Tableau comparatif", "Modifications critiques", "Recommandations"]
    },
    {
      id: "jurisprudence-summary",
      name: "Résumé de Jurisprudence",
      category: "contentieux",
      description: "Analyse et synthèse de décisions de justice : faits, moyens, solution, portée, implications pratiques.",
      icon: Scale,
      color: "amber",
      estimatedTime: "10-15 min",
      steps: 4,
      inputs: ["Décision de justice (PDF)"],
      outputs: ["Fiche de jurisprudence", "Analyse juridique", "Mots-clés"]
    },
    {
      id: "legal-memo-generator",
      name: "Générateur de Note Juridique",
      category: "conseil",
      description: "Création automatique de notes juridiques structurées : contexte, analyse, références légales, conclusion.",
      icon: FileText,
      color: "indigo",
      estimatedTime: "15-25 min",
      steps: 5,
      inputs: ["Question juridique", "Documents de référence"],
      outputs: ["Note juridique complète", "Références", "Bibliographie"]
    },
    {
      id: "clause-library-builder",
      name: "Bibliothèque de Clauses",
      category: "transactionnel",
      description: "Extraction et classification automatique de clauses contractuelles pour créer votre bibliothèque personnalisée.",
      icon: BookOpen,
      color: "teal",
      estimatedTime: "10-20 min",
      steps: 3,
      inputs: ["Contrats sources", "Catégories souhaitées"],
      outputs: ["Bibliothèque de clauses", "Index", "Recommandations d'usage"]
    },
    {
      id: "litigation-risk-assessment",
      name: "Évaluation Risque Contentieux",
      category: "contentieux",
      description: "Analyse prédictive du risque contentieux : chances de succès, coûts estimés, stratégie recommandée.",
      icon: TrendingUp,
      color: "orange",
      estimatedTime: "20-30 min",
      steps: 6,
      inputs: ["Dossier contentieux", "Jurisprudence applicable"],
      outputs: ["Score de risque", "Analyse prédictive", "Recommandations stratégiques"]
    },
    {
      id: "corporate-compliance-check",
      name: "Vérification Conformité Sociétés",
      category: "conformite",
      description: "Audit de conformité corporate : statuts, PV, registres, obligations légales, délais à respecter.",
      icon: Building2,
      color: "cyan",
      estimatedTime: "15-25 min",
      steps: 5,
      inputs: ["Documents sociaux", "K-bis"],
      outputs: ["Rapport de conformité", "Non-conformités", "Échéancier"]
    },
    {
      id: "contract-negotiation-prep",
      name: "Préparation Négociation",
      category: "transactionnel",
      description: "Analyse du contrat adverse et génération de points de négociation : clauses à modifier, arguments, contre-propositions.",
      icon: Zap,
      color: "pink",
      estimatedTime: "10-20 min",
      steps: 4,
      inputs: ["Projet de contrat", "Objectifs client"],
      outputs: ["Points de négociation", "Arguments juridiques", "Contre-propositions"]
    }
  ];

  const categories = [
    { id: "all", label: "Tous", icon: Workflow },
    { id: "transactionnel", label: "Transactionnel", icon: FileText },
    { id: "contentieux", label: "Contentieux", icon: Scale },
    { id: "conformite", label: "Conformité", icon: Shield },
    { id: "conseil", label: "Conseil", icon: BookOpen }
  ];

  const colorClasses = {
    blue: "bg-blue-100 text-blue-800 border-blue-200",
    red: "bg-red-100 text-red-800 border-red-200",
    green: "bg-green-100 text-green-800 border-green-200",
    purple: "bg-purple-100 text-purple-800 border-purple-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-200",
    teal: "bg-teal-100 text-teal-800 border-teal-200",
    orange: "bg-orange-100 text-orange-800 border-orange-200",
    cyan: "bg-cyan-100 text-cyan-800 border-cyan-200",
    pink: "bg-pink-100 text-pink-800 border-pink-200"
  };

  const filteredWorkflows = workflows.filter(workflow => {
    const matchesCategory = selectedCategory === "all" || workflow.category === selectedCategory;
    const matchesSearch = workflow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         workflow.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const runWorkflow = (workflowId) => {
    const workflow = workflows.find(w => w.id === workflowId);
    setSelectedWorkflow(workflow);
    setWorkflowStep(0);
    setWorkflowInputs({});
    setWorkflowResults(null);
  };

  const handleWorkflowInput = (field, value) => {
    setWorkflowInputs(prev => ({ ...prev, [field]: value }));
  };

  const executeWorkflow = async () => {
    setWorkflowRunning(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      
      // Call real workflow API
      const response = await axios.post(
        `${API}/workflows/execute`,
        {
          workflow_id: selectedWorkflow.id,
          inputs: workflowInputs,
          language: 'fr'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setWorkflowResults({
        success: true,
        message: `Workflow "${selectedWorkflow.name}" exécuté avec succès !`,
        outputs: response.data.outputs,
        execution_id: response.data.execution_id
      });
      
      toast.success("Workflow terminé avec succès!");
    } catch (error) {
      console.error("Workflow execution error:", error);
      toast.error("Erreur lors de l'exécution du workflow");
      
      // Fallback to demo mode if API fails
      await new Promise(resolve => setTimeout(resolve, 2000));
      setWorkflowResults({
        success: true,
        message: `Workflow "${selectedWorkflow.name}" exécuté (mode démo)`,
        outputs: selectedWorkflow.outputs
      });
    } finally {
      setWorkflowRunning(false);
    }
  };

  const closeWorkflowModal = () => {
    setSelectedWorkflow(null);
    setWorkflowStep(0);
    setWorkflowInputs({});
    setWorkflowResults(null);
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-blue-900 mb-2">Workflows Juridiques</h1>
          <p className="text-slate-600">
            Automatisez vos tâches juridiques avec des workflows IA prêts à l'emploi, 
            personnalisés pour le droit français et européen.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Workflows Disponibles</p>
                  <p className="text-2xl font-bold text-blue-900">{workflows.length}</p>
                </div>
                <Workflow className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Temps Moyen</p>
                  <p className="text-2xl font-bold text-blue-900">15 min</p>
                </div>
                <Clock className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Qualité Experte</p>
                  <p className="text-2xl font-bold text-blue-900">98%</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Gain de Temps</p>
                  <p className="text-2xl font-bold text-blue-900">70%</p>
                </div>
                <TrendingUp className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher un workflow..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(category => {
              const Icon = category.icon;
              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                  className={selectedCategory === category.id ? "bg-blue-600" : ""}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {category.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Workflows Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkflows.map(workflow => {
            const Icon = workflow.icon;
            return (
              <Card key={workflow.id} className="hover:shadow-lg transition-shadow group">
                <CardHeader>
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-3 rounded-lg ${colorClasses[workflow.color]} border`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {workflow.estimatedTime}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg">{workflow.name}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {workflow.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <Workflow className="h-4 w-4" />
                      <span>{workflow.steps} étapes</span>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Résultats :</p>
                    <div className="flex flex-wrap gap-1">
                      {workflow.outputs.slice(0, 2).map((output, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {output}
                        </Badge>
                      ))}
                      {workflow.outputs.length > 2 && (
                        <Badge variant="secondary" className="text-xs">
                          +{workflow.outputs.length - 2}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={() => runWorkflow(workflow.id)}
                    className="w-full bg-blue-600 hover:bg-blue-700 group-hover:shadow-md transition"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Lancer le Workflow
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredWorkflows.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-slate-400">
              <Workflow className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p>Aucun workflow trouvé pour cette recherche</p>
            </CardContent>
          </Card>
        )}

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-blue-900 mb-2">
                  Workflow Builder - Bientôt Disponible
                </h3>
                <p className="text-sm text-slate-700 mb-3">
                  Créez vos propres workflows personnalisés en quelques clics avec notre builder visuel. 
                  Capturez l'expertise unique de votre cabinet et déployez-la à l'échelle.
                </p>
                <Button variant="outline" size="sm" className="border-blue-300 hover:bg-blue-100">
                  En savoir plus
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Execution Modal */}
      {selectedWorkflow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-slate-200 flex items-start justify-between">
              <div className="flex items-start gap-4">
                {(() => {
                  const Icon = selectedWorkflow.icon;
                  return (
                    <div className={`p-3 rounded-lg ${colorClasses[selectedWorkflow.color]} border`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  );
                })()}
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{selectedWorkflow.name}</h2>
                  <p className="text-sm text-slate-600 mt-1">{selectedWorkflow.description}</p>
                </div>
              </div>
              <button onClick={closeWorkflowModal} className="text-slate-400 hover:text-slate-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {!workflowResults ? (
                <div className="space-y-6">
                  {/* Progress Steps */}
                  <div className="flex items-center justify-between mb-8">
                    {Array.from({ length: selectedWorkflow.steps }).map((_, idx) => (
                      <div key={idx} className="flex items-center flex-1">
                        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                          idx <= workflowStep ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'
                        }`}>
                          {idx < workflowStep ? <CheckCircle2 className="h-5 w-5" /> : idx + 1}
                        </div>
                        {idx < selectedWorkflow.steps - 1 && (
                          <div className={`flex-1 h-1 mx-2 ${
                            idx < workflowStep ? 'bg-blue-600' : 'bg-slate-200'
                          }`}></div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Input Fields */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-slate-900">
                      Étape {workflowStep + 1} sur {selectedWorkflow.steps}
                    </h3>
                    
                    {selectedWorkflow.inputs.map((input, idx) => (
                      <div key={idx}>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {input}
                        </label>
                        <textarea
                          value={workflowInputs[input] || ''}
                          onChange={(e) => handleWorkflowInput(input, e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          rows={4}
                          placeholder={`Saisissez ${input.toLowerCase()}...`}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Expected Outputs Preview */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Résultats attendus
                    </h4>
                    <ul className="space-y-2">
                      {selectedWorkflow.outputs.map((output, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-blue-800">
                          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>{output}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      Workflow terminé !
                    </h3>
                    <p className="text-slate-600">{workflowResults.message}</p>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-left">
                    <h4 className="font-semibold text-slate-900 mb-4">Résultats générés :</h4>
                    <div className="space-y-3">
                      {workflowResults.outputs.map((output, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <span className="text-sm text-slate-900">{output}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <p className="text-sm text-slate-500">
                    Note : Cette démo simule l'exécution du workflow. 
                    Dans la version finale, l'IA générera les résultats réels.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="h-4 w-4" />
                <span>Temps estimé : {selectedWorkflow.estimatedTime}</span>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={closeWorkflowModal}>
                  {workflowResults ? 'Fermer' : 'Annuler'}
                </Button>
                
                {!workflowResults && (
                  <Button
                    onClick={executeWorkflow}
                    disabled={workflowRunning || selectedWorkflow.inputs.some(input => !workflowInputs[input])}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {workflowRunning ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Traitement...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Exécuter
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
